/**
 * Onboarding Route Watcher Component
 *
 * Watches route changes and triggers tours based on current route.
 * Integrates with React Router for route-based tour triggering.
 *
 * @author Andres Nava
 * @version 1.0.0
 */

import React, { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useOnboarding } from "../../contexts/OnboardingContext";
import { useAuth } from "../../contexts/AuthContext";
import { getRoleSpecificTourId, getPrimaryRole } from "../../utils/tourHelpers";
import { isLabMissionTour } from "../../../shared/onboarding/tourCatalog";
import { logger } from "../../utils/logger";

/**
 * Route to tour mapping configuration
 */
interface RouteTourMapping {
  /** Route path pattern */
  route: string;
  /** Tour flow ID to trigger */
  tourId: string;
  /** Whether to trigger only if tour not completed */
  triggerIfNotCompleted?: boolean;
}

/**
 * Default route mappings for contextual auto-start tours.
 * Dashboard is intentionally omitted — FirstSessionTourTrigger owns welcome_quick_start;
 * role dashboard tours mismatch when the active role tab differs from getRoleSpecificTourId().
 */
const defaultRouteMappings: RouteTourMapping[] = [
  {
    route: '/coordinator-event-create',
    tourId: 'coordinator_create_event',
    triggerIfNotCompleted: true,
  },
  {
    route: '/dashboard/coordinator-event-create',
    tourId: 'coordinator_create_event',
    triggerIfNotCompleted: true,
  },
  {
    route: '/events/new',
    tourId: 'coordinator_create_event',
    triggerIfNotCompleted: true,
  },
];

/**
 * Onboarding Route Watcher Component
 *
 * Enhanced route watcher with progressive contextual triggers.
 * Supports role-specific tours, query parameter detection, and smart triggering logic.
 */
export const OnboardingRouteWatcher: React.FC = () => {
  const location = useLocation();
  const { state, startTour, isFlowCompleted, config, getAvailableTours } =
    useOnboarding();
  const { user } = useAuth();
  const triggeredRef = useRef<Set<string>>(new Set()); // Track triggered tours per session
  const lastRouteRef = useRef<string>(""); // Track last route to prevent duplicate triggers

  useEffect(() => {
    // Don't trigger if onboarding is disabled or tour is already running
    if (!config.enabled || state.isRunning) {
      return;
    }

    const currentPath = location.pathname;
    const currentRoute = `${currentPath}${location.search}`;

    // Don't trigger tours on home page
    if (currentPath === "/") {
      return;
    }

    // Prevent duplicate triggers on same route
    if (currentRoute === lastRouteRef.current) {
      return;
    }
    lastRouteRef.current = currentRoute;

    // Find matching route mapping
    const matchingMapping = defaultRouteMappings.find((mapping) => {
      if (mapping.route.includes("?")) {
        // Handle query params - check both path and query string
        const [path, queryParam] = mapping.route.split("?");
        const queryKey = queryParam.split("=")[0];
        const queryValue = queryParam.split("=")[1];
        const urlParams = new URLSearchParams(location.search);

        return currentPath === path && urlParams.get(queryKey) === queryValue;
      }
      // Exact match or starts with (for nested routes)
      return (
        currentPath === mapping.route ||
        currentPath.startsWith(mapping.route + "/")
      );
    });

    if (matchingMapping) {
      // Resolve role-specific tour for dashboard
      let tourId = matchingMapping.tourId;
      if (tourId === "" && currentPath === "/dashboard") {
        tourId = getRoleSpecificTourId(user);
      }

      if (!tourId) {
        return;
      }

      // Lab missions start from Help menu only — never auto-start coordinator_* slugs
      if (isLabMissionTour(tourId)) {
        logger.debug("Skipping route watcher auto-start for lab tour", {
          tourId,
          route: currentPath,
        });
        return;
      }

      // Check if tour exists and is available for user
      const availableTours = getAvailableTours();
      const tour = availableTours.find((t) => t.id === tourId);
      if (!tour) {
        logger.debug("Tour not available for user", {
          tourId,
          route: currentPath,
        });
        return;
      }

      // Check if tour should be triggered
      const alreadyTriggered = triggeredRef.current.has(tourId);
      const shouldTrigger =
        !alreadyTriggered &&
        (!matchingMapping.triggerIfNotCompleted || !isFlowCompleted(tourId));

      if (shouldTrigger) {
        logger.info("Route watcher triggering contextual tour", {
          route: currentPath,
          search: location.search,
          tourId,
          userRole: getPrimaryRole(user),
        });

        // Mark as triggered to prevent duplicate triggers
        triggeredRef.current.add(tourId);

        // Wait for page to render before starting tour
        const timer = setTimeout(() => {
          startTour(tourId).catch((error) => {
            logger.error("Error starting tour from route watcher", {
              error,
              tourId,
              route: currentPath,
            });
            // Remove from triggered set if failed to allow retry
            triggeredRef.current.delete(tourId);
          });
        }, 800); // Increased delay to ensure dynamic content is loaded

        return () => clearTimeout(timer);
      }
    }
  }, [
    location.pathname,
    location.search,
    state.isRunning,
    config.enabled,
    user,
    startTour,
    isFlowCompleted,
    getAvailableTours,
  ]);

  // Reset triggered tours when route changes significantly (e.g., logout/login)
  useEffect(() => {
    if (!user) {
      triggeredRef.current.clear();
      lastRouteRef.current = "";
    }
  }, [user]);

  // This component doesn't render anything
  return null;
};

export default OnboardingRouteWatcher;
